/**
 * Ghostwriter backend API and local storage access.
 *
 * All network calls and TareBridge interactions used by the shared
 * hooks live here so that:
 *  1. Hook call-sites stay zero-config (no deps to pass in).
 *  2. Tests can `jest.mock('./ghostwriterApi')` in one place.
 *
 * GraphQL operations use lazy queries and mutations from the generated
 * OneGraph hooks, following the same pattern as AutoApplyOnegraphService.
 */

import { GHOSTWRITER_PREFS_NAMESPACE } from "./ghostwriterLifecycle";
import { type DraftFieldValue, addJobToStore, getSnapshot } from "./GhostwriterStore";
import { hydrateGroupInstanceFields, isFieldVisible } from "./GhostwriterValidation";
import { isProductionEnvironment, normalizeEnvironment } from "../../environment";
import { localPrefs } from "../../native-modules/tare/getLocalPrefs";
import { useTareData } from "../../native-modules/tare/TareDataContext";
import { decodeHtmlEntities } from "../../util/descriptionFormat/decodeHtmlEntities";
import { useCancelAutoApplyMutation } from "../graphql/__generated__/cancelGhostwriterAutoApply.gqlgenerated";
import { useConsentToScheduleApplyMutation } from "../graphql/__generated__/consentToScheduleApply.gqlgenerated";
import {
  type CreateGhostwriterResponseSetMutationVariables,
  useCreateGhostwriterResponseSetMutation,
} from "../graphql/__generated__/createGhostwriterResponseSet.gqlgenerated";
import { useDeleteApplicationMutation } from "../graphql/__generated__/deleteGhostwriterApplication.gqlgenerated";
import { useGhostwriterEligibilityAndApplicationLazyQuery } from "../graphql/__generated__/ghostwriterEligibilityAndApplication.gqlgenerated";
import { useGhostwriterJobEligibilityLazyQuery } from "../graphql/__generated__/ghostwriterJobEligibility.gqlgenerated";
import {
  type GhostwriterViewerApplicationsQuery,
  useGhostwriterViewerApplicationsLazyQuery,
} from "../graphql/__generated__/ghostwriterViewerApplications.gqlgenerated";
import { useGhostwriterViewerConsentStatusLazyQuery } from "../graphql/__generated__/ghostwriterViewerConsentStatus.gqlgenerated";
import { useSubmitGhostwriterApplicationMutation } from "../graphql/__generated__/submitGhostwriterApplication.gqlgenerated";
import { useTriggerGhostwriterApplyMutation } from "../graphql/__generated__/triggerGhostwriterApply.gqlgenerated";
import {
  type ValidationInputType,
  type AutocompleteType,
  type ScheduleApplyFormFieldGroup,
  type ScheduleApplyUnboundFormField,
  type ScheduleApplyOptionsFormField,
  type ScheduleApplyFormFieldOption,
  FieldType,
} from "../models/ScheduleApplyFormField";
import {
  type ScheduleApplyUnboundFormFieldResponse,
  type ScheduleApplyOptionsFormFieldResponse,
  ScheduleApplyFormFieldResponseValidationStatus,
} from "../models/ScheduleApplyFormFieldResponse";
import {
  type InterventionInfo,
  type ScheduleApplyJob,
  type ScheduleApplyResponseGroupInstanceSeed,
  ScheduleApplyStatuses,
  type ScheduleApplyStatus,
} from "../models/ScheduleApplyJob";
import { relayIdSuffix } from "../utils/relayId";

import { useCallback } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EligibilityResult =
  | { eligible: false; application: null }
  | { eligible: true; application: ScheduleApplyJob | null };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CONSENT_KEY = "hasConsented";
// TODO: Implement cursor-based pagination. Queries using this limit silently
// drop applications beyond the first page, causing "not found" errors.
const DEFAULT_APPLICATIONS_PAGE_SIZE = 100;

// ---------------------------------------------------------------------------
// GraphQL type aliases (from generated code)
// ---------------------------------------------------------------------------

type GqlApplication = NonNullable<
  NonNullable<
    GhostwriterViewerApplicationsQuery["ghostwriter"]["viewer"]["applications"]
  >["edges"][number]["node"]
>;
type GqlFormFieldGroup = GqlApplication["formFieldGroups"][number];
type GqlFormField = GqlFormFieldGroup["formFields"][number];
type GqlResponseGroup = NonNullable<GqlApplication["currentResponseSet"]>["responseGroups"][number];
type GqlFieldResponse = GqlResponseGroup["fieldResponses"][number];

// ---------------------------------------------------------------------------
// Type mapping helpers
// ---------------------------------------------------------------------------

const STATUS_MAP: Record<string, ScheduleApplyStatus> = {
  ANALYZING_JOB: ScheduleApplyStatuses.AnalyzingJob,
  READY_TO_REVIEW: ScheduleApplyStatuses.ReadyToReview,
  MISSING_REQUIRED_INFO: ScheduleApplyStatuses.MissingRequiredInfo,
  AUTO_APPLY_FAILED: ScheduleApplyStatuses.AutoApplyFailed,
  AUTO_APPLY_CANCELLED: ScheduleApplyStatuses.AutoApplyCancelled,
  SUBMISSION_FAILED: ScheduleApplyStatuses.SubmissionFailed,
  FILLING_OUT_APPLICATION: ScheduleApplyStatuses.FillingOutApplication,
  SUBMITTING_APPLICATION: ScheduleApplyStatuses.SubmittingApplication,
  RETRY_PENDING_CAPTCHA: ScheduleApplyStatuses.RetryPendingCaptcha,
  APPLIED: ScheduleApplyStatuses.Applied,
  AWAITING_USER_INTERVENTION: ScheduleApplyStatuses.AwaitingUserIntervention,
};

const FIELD_TYPE_MAP: Record<string, FieldType> = {
  TEXT: FieldType.Text,
  EMAIL: FieldType.Email,
  TEL: FieldType.Tel,
  TEXTAREA: FieldType.Textarea,
  RADIO: FieldType.Radio,
  CHECKBOX: FieldType.Checkbox,
  SELECT: FieldType.Select,
  SINGLE_SELECT_LIST: FieldType.SingleSelectList,
  FILE: FieldType.File,
  DATE: FieldType.Date,
  INFORMATION: FieldType.Information,
  SOCIAL_SECURITY_NUMBER: FieldType.SocialSecurityNumber,
  POSTAL_CODE: FieldType.PostalCode,
};

function mapApplicationStatus(status: string): ScheduleApplyStatus {
  return STATUS_MAP[status] ?? ScheduleApplyStatuses.NotStarted;
}

function mapFieldType(type: string): FieldType {
  return FIELD_TYPE_MAP[type] ?? FieldType.Text;
}

function mapValidationStatus(status: string): ScheduleApplyFormFieldResponseValidationStatus {
  return status === "VALID"
    ? ScheduleApplyFormFieldResponseValidationStatus.Valid
    : ScheduleApplyFormFieldResponseValidationStatus.Invalid;
}

/** Cap multi-pass HTML entity decoding to avoid unbounded loops on bad input. */
const MAX_GHOSTWRITER_HTML_ENTITY_DECODE_PASSES = 5;

/**
 * Runs {@link decodeHtmlEntities} repeatedly until no further decoding needed
 * {@link MAX_GHOSTWRITER_HTML_ENTITY_DECODE_PASSES} is reached. Handles
 * double-encoded API text (e.g. `&amp;amp;` → `&`).
 */
function decodeGhostwriterHtmlEntities(text: string): string {
  let decoded = text;
  for (let pass = 0; pass < MAX_GHOSTWRITER_HTML_ENTITY_DECODE_PASSES; pass++) {
    const next = decodeHtmlEntities(decoded);
    if (next === decoded) {
      break;
    }
    decoded = next;
  }
  return decoded;
}

/**
 * Decodes HTML entities in user-facing text from the Ghostwriter API
 * (e.g. `&#x27;` → `'`), returning an empty string for null/undefined.
 */
function decodeGhostwriterText(text: string | null | undefined): string {
  if (!text) {
    return text ?? "";
  }
  return decodeGhostwriterHtmlEntities(text);
}

/**
 * Like `decodeGhostwriterText`, but preserves null for optional fields
 * instead of coercing to an empty string.
 */
function decodeGhostwriterTextOrNull(text: string | null | undefined): string | null {
  if (text == null) {
    return null;
  }
  return decodeGhostwriterHtmlEntities(text);
}

/**
 * Decodes HTML entities in each element of an array (e.g. selectedOptions).
 * Returns an empty array when the input is null or undefined.
 */
function decodeGhostwriterTextArray(values: readonly string[] | null | undefined): string[] {
  if (!values) {
    return [];
  }
  return values.map(decodeGhostwriterText);
}

/** Lowest displayOrder first; null/undefined sort last to keep ordered ones in front. */
function compareDisplayOrder(a: GqlResponseGroup, b: GqlResponseGroup): number {
  const ao = a.displayOrder;
  const bo = b.displayOrder;
  if (ao == null && bo == null) return 0;
  if (ao == null) return 1;
  if (bo == null) return -1;
  return ao - bo;
}

/**
 * Walks every form field group (including nested) and returns a lookup
 * `formFieldId-suffix → owning formFieldGroupId`. Used to attribute each
 * response group to its parent form field group via the field IDs in
 * its `fieldResponses`. The schema does not expose the link directly on
 * `GhostwriterResponseGroup`, so we derive it.
 */
function buildFieldToGroupIndex(formFieldGroups: GqlFormFieldGroup[]): Map<string, string> {
  const index = new Map<string, string>();
  function walk(groups: readonly GqlFormFieldGroup[]) {
    for (const group of groups) {
      for (const field of group.formFields) {
        index.set(relayIdSuffix(field.id), group.id);
      }
      if (group.nestedFormFieldGroup) {
        walk([group.nestedFormFieldGroup]);
      }
    }
  }
  walk(formFieldGroups);
  return index;
}

/**
 * Buckets the application's response groups by their owning form field
 * group ID. Each bucket is sorted ascending by `displayOrder` so the
 * first entry is the primary instance.
 *
 * Response groups whose `fieldResponses` reference no known form field
 * (e.g. a data drift between schema and response) are dropped silently.
 */
function bucketResponseGroupsByFormFieldGroup(
  app: GqlApplication,
): Map<string, GqlResponseGroup[]> {
  const fieldToGroup = buildFieldToGroupIndex(app.formFieldGroups);
  const buckets = new Map<string, GqlResponseGroup[]>();
  const responseGroups = app.currentResponseSet?.responseGroups ?? [];
  for (const rg of responseGroups) {
    let owningGroupId: string | undefined;
    for (const fr of rg.fieldResponses) {
      const candidate = fieldToGroup.get(relayIdSuffix(fr.formField.id));
      if (candidate) {
        owningGroupId = candidate;
        break;
      }
    }
    if (!owningGroupId) continue;
    const bucket = buckets.get(owningGroupId);
    if (bucket) {
      bucket.push(rg);
    } else {
      buckets.set(owningGroupId, [rg]);
    }
  }
  for (const bucket of buckets.values()) {
    bucket.sort(compareDisplayOrder);
  }
  return buckets;
}

/**
 * Builds the per-form-field response map used by `mapFormField` to
 * populate each field's `response`. Sourced from the *primary* (lowest
 * `displayOrder`) response group of each bucket. Secondary instances
 * for repeatable groups live separately in `responseGroupSeeds`.
 */
function buildFieldResponseMap(
  buckets: ReadonlyMap<string, ReadonlyArray<GqlResponseGroup>>,
): Map<string, GqlFieldResponse> {
  const map = new Map<string, GqlFieldResponse>();
  for (const bucket of buckets.values()) {
    const primary = bucket[0];
    if (!primary) continue;
    for (const fr of primary.fieldResponses) {
      map.set(relayIdSuffix(fr.formField.id), fr);
    }
  }
  return map;
}

/**
 * Builds the seeds map consumed by the draft store to hydrate
 * additional instances (`displayOrder >= 1`) of repeatable groups.
 * Non-repeatable groups have an empty (or absent) seed list — their
 * single response is already represented in `fieldResponseMap`.
 *
 * `fieldValues` is keyed by `relayIdSuffix(formField.id)` so the store
 * can match each value against the model field via the same suffix
 * trick used by `mapFormField` — the response-side IDs may carry a
 * different type prefix than the form-field-side IDs.
 */
function buildResponseGroupSeeds(
  buckets: ReadonlyMap<string, ReadonlyArray<GqlResponseGroup>>,
): Map<string, ScheduleApplyResponseGroupInstanceSeed[]> {
  const seeds = new Map<string, ScheduleApplyResponseGroupInstanceSeed[]>();
  let counter = 0;
  for (const [groupId, bucket] of buckets) {
    if (bucket.length <= 1) continue;
    const groupSeeds: ScheduleApplyResponseGroupInstanceSeed[] = [];
    for (let i = 1; i < bucket.length; i++) {
      const rg = bucket[i];
      const fieldValues = new Map<string, string | string[]>();
      for (const fr of rg.fieldResponses) {
        const suffix = relayIdSuffix(fr.formField.id);
        if (fr.selectedOptions != null && fr.selectedOptions.length > 0) {
          fieldValues.set(suffix, decodeGhostwriterTextArray(fr.selectedOptions));
        } else {
          fieldValues.set(suffix, decodeGhostwriterText(fr.responseValue));
        }
      }
      groupSeeds.push({
        instanceId: `seed-${++counter}`,
        displayOrder: rg.displayOrder ?? null,
        name: decodeGhostwriterTextOrNull(rg.name),
        fieldValues,
      });
    }
    seeds.set(groupId, groupSeeds);
  }
  return seeds;
}

/**
 * Returns true when *any* response group in the bucket has the
 * repeatability flag set. Defaults to `false` for groups with no
 * matching response group (the application has none persisted yet).
 */
function deriveCanCreateNewResponseGroup(
  bucket: ReadonlyArray<GqlResponseGroup> | undefined,
): boolean {
  if (!bucket || bucket.length === 0) return false;
  return bucket.some((rg) => rg.canCreateNewResponseGroup);
}

function isOptionsFieldType(type: FieldType): boolean {
  return (
    type === FieldType.Radio ||
    type === FieldType.Checkbox ||
    type === FieldType.Select ||
    type === FieldType.SingleSelectList
  );
}

function mapFormField(
  gqlField: GqlFormField,
  fieldResponseMap: Map<string, GqlFieldResponse>,
): ScheduleApplyUnboundFormField | ScheduleApplyOptionsFormField {
  let fieldType = mapFieldType(gqlField.fieldType);
  const gqlValidation = gqlField.validationInputType;
  if (gqlValidation === "SOCIAL_SECURITY_NUMBER") {
    fieldType = FieldType.SocialSecurityNumber;
  } else if (gqlValidation === "POSTAL_CODE") {
    fieldType = FieldType.PostalCode;
  }
  const resp = fieldResponseMap.get(relayIdSuffix(gqlField.id));

  const base = {
    id: gqlField.id,
    fieldName: gqlField.fieldName ?? "",
    fieldType,
    classification: gqlField.classification ?? "",
    autocompleteType: (gqlField.autocompleteType ?? null) as AutocompleteType | null,
    validationInputType: (gqlField.validationInputType ?? null) as ValidationInputType | null,
    label: decodeGhostwriterText(gqlField.label),
    description: decodeGhostwriterTextOrNull(gqlField.description),
    required: gqlField.required,
    disabled: gqlField.disabled,
    readonly: gqlField.readonly,
    condition: gqlField.condition
      ? {
          fieldName: gqlField.condition.fieldName,
          value: decodeGhostwriterText(gqlField.condition.value),
          operator: gqlField.condition.operator,
        }
      : null,
    confirmsFieldName: gqlField.confirmsFieldName ?? null,
  };

  if (isOptionsFieldType(fieldType)) {
    return {
      ...base,
      options: (gqlField.options ?? []).map(
        (o): ScheduleApplyFormFieldOption => ({
          value: decodeGhostwriterText(o.value),
          label: decodeGhostwriterText(o.label),
        }),
      ),
      response: mapOptionsResponse(gqlField, resp),
    };
  }

  return {
    ...base,
    placeholder: decodeGhostwriterText(gqlField.placeholder),
    response: mapUnboundResponse(gqlField, resp),
  };
}

function mapUnboundResponse(
  gqlField: GqlFormField,
  resp?: GqlFieldResponse,
): ScheduleApplyUnboundFormFieldResponse {
  return {
    formFieldId: gqlField.id,
    formField: gqlField.fieldName ?? "",
    validationStatus: resp
      ? mapValidationStatus(resp.validationStatus)
      : ScheduleApplyFormFieldResponseValidationStatus.Valid,
    responseValue: decodeGhostwriterText(resp?.responseValue ?? gqlField.value ?? ""),
  };
}

function mapOptionsResponse(
  gqlField: GqlFormField,
  resp?: GqlFieldResponse,
): ScheduleApplyOptionsFormFieldResponse {
  return {
    formFieldId: gqlField.id,
    formField: gqlField.fieldName ?? "",
    validationStatus: resp
      ? mapValidationStatus(resp.validationStatus)
      : ScheduleApplyFormFieldResponseValidationStatus.Valid,
    responseValues: decodeGhostwriterTextArray(resp?.selectedOptions),
  };
}

function mapFormFieldGroup(
  gqlGroup: GqlFormFieldGroup,
  fieldResponseMap: Map<string, GqlFieldResponse>,
  buckets: ReadonlyMap<string, ReadonlyArray<GqlResponseGroup>>,
): ScheduleApplyFormFieldGroup {
  return {
    groupId: gqlGroup.id,
    groupLabel: decodeGhostwriterText(gqlGroup.groupLabel),
    isCollapsed: gqlGroup.isCollapsed,
    canCreateNewResponseGroup: deriveCanCreateNewResponseGroup(buckets.get(gqlGroup.id)),
    formFields: gqlGroup.formFields.map((f) => mapFormField(f, fieldResponseMap)),
    nestedFormFieldGroup: gqlGroup.nestedFormFieldGroup
      ? mapFormFieldGroup(gqlGroup.nestedFormFieldGroup, fieldResponseMap, buckets)
      : null,
  };
}

// TODO: Ideally the GraphQL schema would expose a viewjob URL directly on the job,
// removing the need to construct it client-side.
function getViewJobBaseUrl(buildEnvironment?: string): string {
  return isProductionEnvironment(normalizeEnvironment(buildEnvironment))
    ? "https://www.indeed.com"
    : "https://www.qa.indeed.net";
}

function mapIntervention(raw: GqlApplication["intervention"]): InterventionInfo | null {
  if (!raw) return null;
  return {
    liveUrl: raw.liveUrl,
    kind: raw.kind,
    // `expiresAt` is a nullable DateTime on the server.
    expiresAt: raw.expiresAt ?? null,
  };
}

function mapApplicationToJob(app: GqlApplication, buildEnvironment?: string): ScheduleApplyJob {
  const { job: ghostwriterJob } = app;
  const { job } = ghostwriterJob;
  const buckets = bucketResponseGroupsByFormFieldGroup(app);
  const fieldResponseMap = buildFieldResponseMap(buckets);
  const responseGroupSeeds = buildResponseGroupSeeds(buckets);

  return {
    jobKey: job.key,
    jobUrl: `${getViewJobBaseUrl(buildEnvironment)}/viewjob?jk=${job.key}`,
    title: job.title,
    descriptionHtml: job.description.html,
    location: job.location.formatted.long,
    company: job.sourceEmployerName,
    salary: job.compensation?.formattedText ?? null,
    applicationId: app.id,
    applicationUpdatedDate: app.updatedAt,
    applicationCreatedDate: app.createdAt,
    applicationNotes: decodeGhostwriterTextOrNull(app.applicationNotes),
    applicationStatus: mapApplicationStatus(app.applicationStatus),
    externalApplicationId: app.externalApplicationId ?? null,
    applicationFormFieldGroups: app.formFieldGroups.map((g) =>
      mapFormFieldGroup(g, fieldResponseMap, buckets),
    ),
    responseGroupSeeds,
    intervention: mapIntervention(app.intervention),
  };
}

// ---------------------------------------------------------------------------
// Response-set helpers
// ---------------------------------------------------------------------------

/**
 * Builds the GraphQL input for `createGhostwriterResponseSet`, fanning out
 * one entry per (form-field-group × instance) for repeatable groups.
 *
 * **Wire encoding** (per `GhostwriterResponseGroupInput`): every instance
 * of a repeatable group is submitted as a separate `responseGroup` entry
 * with the **same** `fieldGroupId` and a **sequential** `displayOrder`
 * (0, 1, 2, …) reflecting the on-screen order. `formFieldId`s inside
 * each entry are always bare — there is no `#`-suffix encoding on the
 * wire, regardless of the client's draft-key encoding.
 *
 * Conditional fields whose visibility evaluates to hidden (per the
 * RN-3317 / GHOST-36 spec) are excluded from each instance's payload so
 * stale drafts don't leak.
 */
// Exported only for unit tests; not part of the public API.
export function buildResponseGroupsInput(
  formFieldGroups: ScheduleApplyFormFieldGroup[],
  fieldUpdates: ReadonlyMap<string, DraftFieldValue>,
  groupInstances?: ReadonlyMap<string, ReadonlyArray<string>>,
): CreateGhostwriterResponseSetMutationVariables["input"]["responseGroups"] {
  function processGroupInstance(
    group: ScheduleApplyFormFieldGroup,
    instanceSuffix: string | null,
    displayOrder: number,
  ): CreateGhostwriterResponseSetMutationVariables["input"]["responseGroups"][number] | null {
    const fieldResponses: Array<{
      formFieldId: string;
      responseValue?: string | null;
      selectedOptions?: string[] | null;
    }> = [];

    const hydratedSiblings = hydrateGroupInstanceFields(group, instanceSuffix, fieldUpdates);

    for (let i = 0; i < group.formFields.length; i++) {
      const field = group.formFields[i];
      if (!isFieldVisible(hydratedSiblings[i], hydratedSiblings)) continue;

      const key = instanceSuffix == null ? field.id : `${field.id}#${instanceSuffix}`;
      const value = fieldUpdates.get(key);
      if (value === undefined) continue;

      if (Array.isArray(value)) {
        fieldResponses.push({ formFieldId: field.id, selectedOptions: value });
      } else {
        fieldResponses.push({ formFieldId: field.id, responseValue: value });
      }
    }

    if (fieldResponses.length === 0) return null;
    return {
      fieldGroupId: group.groupId,
      displayOrder,
      formFieldResponses: fieldResponses,
    };
  }

  const groups: CreateGhostwriterResponseSetMutationVariables["input"]["responseGroups"] = [];

  function walkGroups(formFieldGroups: ScheduleApplyFormFieldGroup[]) {
    for (const group of formFieldGroups) {
      const instances = groupInstances?.get(group.groupId);
      if (instances && instances.length > 1) {
        for (let i = 0; i < instances.length; i++) {
          const isPrimary = i === 0;
          const suffix = isPrimary ? null : instances[i];
          const mapped = processGroupInstance(group, suffix, i);
          if (mapped) groups.push(mapped);
        }
      } else {
        const mapped = processGroupInstance(group, null, 0);
        if (mapped) groups.push(mapped);
      }
      if (group.nestedFormFieldGroup) {
        walkGroups([group.nestedFormFieldGroup]);
      }
    }
  }

  walkGroups(formFieldGroups);
  return groups;
}

// ---------------------------------------------------------------------------
// GraphQL error helper
// ---------------------------------------------------------------------------

const TAG = "GhostwriterApi";

function throwIfUserErrors(label: string, userErrors: ReadonlyArray<{ message: string }>) {
  if (userErrors.length > 0) {
    const msg = userErrors.map((e) => e.message).join("; ");
    console.error(TAG, label, "userErrors:", msg);
    throw new Error(msg);
  }
}

/**
 * Thrown by `submitApplication` when the server returns userErrors.
 * Carries the application's updated status when the backend includes it
 * in the error payload (e.g. SUBMISSION_FAILED after an IA draft gate failure),
 * so callers can update the local store without a separate fetch.
 */
export class SubmitApplicationUserError extends Error {
  readonly applicationStatus: ScheduleApplyStatus | undefined;

  constructor(message: string, applicationStatus?: ScheduleApplyStatus) {
    super(message);
    this.name = "SubmitApplicationUserError";
    this.applicationStatus = applicationStatus;
  }
}

// ---------------------------------------------------------------------------
// Hook: useGhostwriterApi
// ---------------------------------------------------------------------------

export function useGhostwriterApi() {
  const tareData = useTareData();
  const buildEnvironment = tareData.appModel?.buildEnvironment;
  const [queryEligibility] = useGhostwriterJobEligibilityLazyQuery();
  const [queryApplications] = useGhostwriterViewerApplicationsLazyQuery();
  const [queryEligibilityAndApplications] = useGhostwriterEligibilityAndApplicationLazyQuery();
  const [queryConsentStatus] = useGhostwriterViewerConsentStatusLazyQuery();
  const [mutateConsent] = useConsentToScheduleApplyMutation();
  const [mutateTriggerApply] = useTriggerGhostwriterApplyMutation();
  const [mutateCreateResponseSet] = useCreateGhostwriterResponseSetMutation();
  const [mutateSubmitApplication] = useSubmitGhostwriterApplicationMutation();
  const [mutateDeleteApplication] = useDeleteApplicationMutation();
  const [mutateCancelAutoApply] = useCancelAutoApplyMutation();

  const fetchAutoAppliableJobKeys = useCallback(
    async (jobKeys: string[]): Promise<string[]> => {
      const { data, error } = await queryEligibility({
        variables: { indeedJobKeys: jobKeys },
        fetchPolicy: "network-only",
      });
      if (error) throw error;
      return data?.ghostwriter.jobEligibility ?? [];
    },
    [queryEligibility],
  );

  const fetchApplicationForJob = useCallback(
    async (jobKey: string): Promise<ScheduleApplyJob | null> => {
      const { data, error } = await queryApplications({
        variables: { first: DEFAULT_APPLICATIONS_PAGE_SIZE },
        fetchPolicy: "network-only",
      });
      if (error) throw error;

      const applications = data?.ghostwriter.viewer.applications?.edges ?? [];
      const match = applications.find((edge) => edge.node.job.job.key === jobKey);

      return match ? mapApplicationToJob(match.node, buildEnvironment) : null;
    },
    [queryApplications, buildEnvironment],
  );

  const fetchApplicationById = useCallback(
    async (applicationId: string): Promise<ScheduleApplyJob> => {
      const { data, error } = await queryApplications({
        variables: { first: DEFAULT_APPLICATIONS_PAGE_SIZE },
        fetchPolicy: "network-only",
      });
      if (error) throw error;

      const applications = data?.ghostwriter.viewer.applications?.edges ?? [];
      const match = applications.find((edge) => edge.node.id === applicationId);

      if (!match) {
        console.error(
          TAG,
          "fetchApplicationById → no match for id",
          applicationId,
          "| available ids:",
          applications.map((e) => e.node.id),
        );
        throw new Error(`No ghostwriter application found for id "${applicationId}"`);
      }
      return mapApplicationToJob(match.node, buildEnvironment);
    },
    [queryApplications, buildEnvironment],
  );

  const fetchEligibilityAndApplication = useCallback(
    async (jobKey: string): Promise<EligibilityResult> => {
      const { data, error } = await queryEligibilityAndApplications({
        variables: {
          jobKeys: [jobKey],
          applicationsFirst: DEFAULT_APPLICATIONS_PAGE_SIZE,
        },
        fetchPolicy: "network-only",
      });
      if (error) throw error;

      const eligibleKeys = data?.ghostwriter.jobEligibility ?? [];
      if (!eligibleKeys.includes(jobKey)) {
        return { eligible: false, application: null };
      }

      const applications = data?.ghostwriter.viewer.applications?.edges ?? [];
      const match = applications.find((edge) => edge.node.job.job.key === jobKey);

      return {
        eligible: true,
        application: match ? mapApplicationToJob(match.node, buildEnvironment) : null,
      };
    },
    [queryEligibilityAndApplications, buildEnvironment],
  );

  const fetchUserApplications = useCallback(async (): Promise<ScheduleApplyJob[]> => {
    const { data, error } = await queryApplications({
      variables: { first: DEFAULT_APPLICATIONS_PAGE_SIZE },
      fetchPolicy: "network-only",
    });
    if (error) throw error;

    const applications = data?.ghostwriter.viewer.applications?.edges ?? [];
    return applications.map((edge) => mapApplicationToJob(edge.node, buildEnvironment));
  }, [queryApplications, buildEnvironment]);

  const fetchConsentStatus = useCallback(async (): Promise<{ hasConsented: boolean }> => {
    const { data, error } = await queryConsentStatus({ fetchPolicy: "network-only" });
    if (error) throw error;
    return { hasConsented: data?.ghostwriter.viewer.consentStatus ?? false };
  }, [queryConsentStatus]);

  const grantConsentOnBackend = useCallback(async (): Promise<void> => {
    const { data, errors } = await mutateConsent();
    if (errors?.length) throw new Error(errors.map((e) => e.message).join("; "));
    const payload = data?.ghostwriter.consentToScheduleApply;
    if (!payload) throw new Error("grantConsentOnBackend: no response data");
    throwIfUserErrors("grantConsentOnBackend", payload.userErrors);
  }, [mutateConsent]);

  const startScheduleApply = useCallback(
    async (jobKey: string): Promise<void> => {
      const { data, errors } = await mutateTriggerApply({
        variables: { input: { jobKey } },
      });
      if (errors?.length) throw new Error(errors.map((e) => e.message).join("; "));
      const payload = data?.ghostwriter.triggerApply;
      if (!payload) throw new Error("startScheduleApply: no response data");
      throwIfUserErrors("startScheduleApply", payload.userErrors);

      const triggeredApplication = payload.application;
      if (!triggeredApplication) return;

      const applicationStatus = mapApplicationStatus(triggeredApplication.applicationStatus);
      const existingJob = getSnapshot().jobs.data?.find((job) => job.jobKey === jobKey);
      if (existingJob) {
        addJobToStore({
          ...existingJob,
          applicationId: triggeredApplication.id,
          applicationStatus,
        });
        return;
      }

      const fetchedJob = await fetchApplicationForJob(jobKey);
      if (fetchedJob) {
        addJobToStore(fetchedJob);
      }
    },
    [mutateTriggerApply, fetchApplicationForJob],
  );

  const createResponseSet = useCallback(
    async (
      applicationId: string,
      formFieldGroups: ScheduleApplyFormFieldGroup[],
      fieldUpdates: ReadonlyMap<string, DraftFieldValue>,
      isUserApproved: boolean = false,
      groupInstances?: ReadonlyMap<string, ReadonlyArray<string>>,
    ): Promise<{ responseSetId: string }> => {
      const responseGroups = buildResponseGroupsInput(
        formFieldGroups,
        fieldUpdates,
        groupInstances,
      );

      const { data, errors } = await mutateCreateResponseSet({
        variables: {
          input: { applicationId, isUserApproved, responseGroups },
        },
      });
      if (errors?.length) throw new Error(errors.map((e) => e.message).join("; "));

      const payload = data?.ghostwriter.createResponseSet;
      if (!payload) throw new Error("createResponseSet: no response data");
      throwIfUserErrors("createResponseSet", payload.userErrors);

      const responseSetId = payload.responseSet?.id;
      if (!responseSetId) {
        console.error(TAG, "createResponseSet → no responseSet in response", JSON.stringify(data));
        throw new Error("createResponseSet: no responseSet returned");
      }
      return { responseSetId };
    },
    [mutateCreateResponseSet],
  );

  const submitApplication = useCallback(
    async (
      applicationId: string,
      formFieldGroups: ScheduleApplyFormFieldGroup[],
      fieldUpdates: ReadonlyMap<string, DraftFieldValue>,
      groupInstances?: ReadonlyMap<string, ReadonlyArray<string>>,
    ): Promise<ScheduleApplyStatus | null> => {
      const { responseSetId } = await createResponseSet(
        applicationId,
        formFieldGroups,
        fieldUpdates,
        true,
        groupInstances,
      );

      const { data, errors } = await mutateSubmitApplication({
        variables: {
          input: { applicationId, responseSetId, dryRun: false },
        },
      });
      if (errors?.length) throw new Error(errors.map((e) => e.message).join("; "));

      const payload = data?.ghostwriter.submitApplication;
      if (!payload) throw new Error("submitApplication: no response data");
      if (payload.userErrors.length > 0) {
        const msg = payload.userErrors.map((e) => e.message).join("; ");
        console.error(TAG, "submitApplication", "userErrors:", msg);
        const applicationStatus = payload.application?.applicationStatus
          ? mapApplicationStatus(payload.application.applicationStatus)
          : undefined;
        throw new SubmitApplicationUserError(msg, applicationStatus);
      }
      // Return the resulting status (typically `SubmittingApplication`)
      // so the caller can reflect it in the store and detect failure. The
      // status flips to `AwaitingUserIntervention` asynchronously later;
      // the phase_d post-submission poll — not this return value — drives
      // the InterventionScreen routing. See RN-3367.
      return payload.application?.applicationStatus
        ? mapApplicationStatus(payload.application.applicationStatus)
        : null;
    },
    [createResponseSet, mutateSubmitApplication],
  );

  const deleteApplication = useCallback(
    async (applicationId: string): Promise<void> => {
      const { data, errors } = await mutateDeleteApplication({
        variables: { deleteApplicationId: applicationId },
      });
      if (errors?.length) throw new Error(errors.map((e) => e.message).join("; "));
      const payload = data?.ghostwriter.deleteApplication;
      if (!payload) throw new Error("deleteApplication: no response data");
      throwIfUserErrors("deleteApplication", payload.userErrors);
    },
    [mutateDeleteApplication],
  );

  const cancelAutoApply = useCallback(
    async (applicationId: string): Promise<void> => {
      const { data, errors } = await mutateCancelAutoApply({
        variables: { input: { applicationId } },
      });
      if (errors?.length) throw new Error(errors.map((e) => e.message).join("; "));
      const payload = data?.ghostwriter.cancelAutoApply;
      if (!payload) throw new Error("cancelAutoApply: no response data");
      throwIfUserErrors("cancelAutoApply", payload.userErrors);
    },
    [mutateCancelAutoApply],
  );

  return {
    fetchAutoAppliableJobKeys,
    fetchApplicationForJob,
    fetchApplicationById,
    fetchEligibilityAndApplication,
    fetchUserApplications,
    fetchConsentStatus,
    grantConsentOnBackend,
    startScheduleApply,
    createResponseSet,
    submitApplication,
    deleteApplication,
    cancelAutoApply,
  };
}

// ---------------------------------------------------------------------------
// TareBridge local preferences — consent persistence
// ---------------------------------------------------------------------------

export async function getLocalConsent(): Promise<boolean | null> {
  const raw = await localPrefs.getString(GHOSTWRITER_PREFS_NAMESPACE, CONSENT_KEY);
  if (raw === null || raw === "") return null;
  return raw === "true";
}

export async function setLocalConsent(value: boolean): Promise<void> {
  await localPrefs.setString(GHOSTWRITER_PREFS_NAMESPACE, CONSENT_KEY, String(value));
}
